import { gql } from '@urql/core';
import React from 'react';
import { useAppState } from '../../../../client/providers/AppProvider';
import {
    Content_File,
    Espin_Game_Definition_Enum,
    Espin_Profile_View_Type_Enum,
    ProfileViewCardFragment,
} from '../../../../generated/spin-graphql';
import { SocialCard, SocialCardData } from './SocialCard';

type Props = {
    profileView: ProfileViewCardFragment;
    viewedAt: any;
};

gql`
    fragment ProfileViewCard on espin_profile_view {
        profile_view_type
        player_user {
            deleted_at
            player_profile {
                user_id
                graduating_class
                first_name
                last_name
                slug
                profile_image {
                    ...SpinContentImageDisplay
                }
                player_games {
                    primary

                    game {
                        name
                        game_definition
                    }
                }
            }
        }
        college_user_full_name
        id
        high_school_user {
            deleted_at
        }
        high_school {
            name
            contact_name
            slug
            profile_pic_file {
                ...SpinContentImageDisplay
            }
        }
        college {
            name
            slug
            logo {
                ...SpinContentImageDisplay
            }
        }
        college_user {
            deleted_at
        }
    }
`;

export const ProfileViewCard: React.FC<Props> = ({ profileView, viewedAt }) => {
    const { subscribed } = useAppState();
    const viewType = profileView?.profile_view_type;
    let fullName: string = '',
        collegeName: string | null,
        slug: string = null,
        profileImg: Partial<Content_File>,
        getPrimaryGame: Espin_Game_Definition_Enum | null,
        primaryGameName: string | null,
        hasGraduatingClass: boolean = false,
        graduatingYear: string,
        isParent: boolean = false,
        deletedUser: boolean = false;

    if (viewType === Espin_Profile_View_Type_Enum.PlayerViewsCollege) {
        fullName =
            `${profileView?.player_user?.player_profile?.first_name} ${profileView?.player_user?.player_profile?.last_name}` ??
            'N/A';
        slug = `players/${profileView?.player_user?.player_profile?.slug}`;
        profileImg = subscribed ? profileView?.player_user?.player_profile?.profile_image : null;
        profileView.player_user?.player_profile?.player_games?.forEach((game) => {
            if (game.primary) {
                getPrimaryGame = game.game.game_definition;
                primaryGameName = game.game.name;
            }
        });
        hasGraduatingClass = true;
        graduatingYear = profileView?.player_user?.player_profile?.graduating_class;
        if (!!profileView?.player_user?.deleted_at) {
            deletedUser = true;
        }
    } else if (viewType === Espin_Profile_View_Type_Enum.ParentViewsCollege) {
        fullName =
            `${profileView?.player_user?.player_profile?.first_name} ${profileView?.player_user?.player_profile?.last_name}'s Parent` ??
            'N/A';
        slug = `players/${profileView?.player_user?.player_profile?.slug}`;
        profileImg = subscribed ? profileView?.player_user?.player_profile?.profile_image : null;
        graduatingYear = profileView?.player_user?.player_profile?.graduating_class;
        if (!!profileView?.player_user?.deleted_at) {
            deletedUser = true;
        }
    } else if (viewType === Espin_Profile_View_Type_Enum.CollegeViewsPlayer) {
        // if college is null, then it's likely been deleted or unverified somehow....
        // i think in terms of data integrity, it's easiest to default this somehow.
        if (!!!profileView?.college) {
            collegeName = 'Unverified College';
            fullName = '';
            profileImg = null;
            slug = `colleges/`;
        } else {
            collegeName = profileView?.college.name;
            fullName = profileView?.college_user_full_name ?? 'N/A';
            profileImg = subscribed ? profileView?.college?.logo : null;
            slug = `colleges/${profileView?.college?.slug}`;
            if (!!profileView?.college_user?.deleted_at) {
                deletedUser = true;
            }
        }
    } else if (viewType === Espin_Profile_View_Type_Enum.CollegeViewsHighSchool) {
        if (!!!profileView?.college) {
            collegeName = 'Unverified College';
            fullName = '';
            profileImg = null;
            slug = `colleges/`;
        } else {
            collegeName = profileView?.college.name;
            fullName = profileView?.college_user_full_name ?? 'N/A';
            profileImg = subscribed ? profileView?.college?.logo : null;
            slug = `colleges/${profileView?.college?.slug}`;
            if (!!profileView?.college_user?.deleted_at) {
                deletedUser = true;
            }
        }
    } else if (viewType === Espin_Profile_View_Type_Enum.HighSchoolViewsCollege) {
        if (!!!profileView?.high_school) {
            collegeName = 'Unverified High School';
            fullName = '';
            profileImg = null;
            slug = `dashboard`;
        } else {
            collegeName = profileView?.high_school.name;
            fullName = profileView?.high_school.contact_name ?? 'N/A';
            profileImg = subscribed ? profileView?.high_school.profile_pic_file : null;
            slug = `high-schools/${profileView?.high_school.slug}`;
            if (!!profileView?.high_school_user?.deleted_at) {
                deletedUser = true;
            }
        }
    }

    const data: SocialCardData = {
        label: 'New Profile View!',
        subLabel: fullName,
        picAltText: `${fullName}'s picture`,
        profileImg,
        slug,
        unread: !viewedAt,
        collegeName,
        primaryGameName,
        getPrimaryGame,
        graduatingYear,
        hasGraduatingClass,
        deletedUser,
    };

    return (
        <>
            <SocialCard data={data} />
        </>
    );
};
